import { createTheme, alpha } from '@mui/material/styles';
import { appTypography, APP_FONT_FAMILY } from './appTypography';

// ── Raw colour tokens — mirrors prototype :root ──────────────────────────────

export const green = {
  50:  '#EAF7EC',
  100: '#CFEDD3',
  200: '#A4DCAB',
  300: '#6BC576',
  400: '#3FB04C',
  500: '#269D32', // primary brand
  600: '#1F7E29',
  700: '#0e6346', // secondary brand (deep)
  800: '#0A4A35',
  900: '#063125',
};

export const ink = {
  900: '#14171A',
  800: '#2A2F35',
  700: '#4A5159',
  600: '#6B737C',
  500: '#8E96A0',
  400: '#B5BCC4',
  300: '#D5DAE0',
  200: '#E8EBEF',
  100: '#F3F5F7',
  50:  '#FAFBFC',
};

export const paper = '#FCFCFB';

export const amber = { 50: '#FBF4E8', 500: '#C68A2E', 700: '#8A5E1E' };
export const rose  = { 50: '#FBEFEC', 500: '#C4624A', 700: '#8A3F2C' };
export const blue  = { 50: '#EDF2F7', 500: '#4A6B8E', 700: '#2A4A6B' };

// Border radius scale — mirrors --radius-sm/md/lg/xl
export const radii = { sm: 6, md: 10, lg: 14, xl: 20 };

// Font stacks — unified to the consultant dashboard font (Inter) across the app.
// fontDisplay previously used Fraunces for headings; now Inter for uniformity.
const fontDisplay = APP_FONT_FAMILY;
const fontBody    = APP_FONT_FAMILY;

// ── Theme ────────────────────────────────────────────────────────────────────

export const abisinyaTheme = createTheme({
  // ── Palette ────────────────────────────────────────────────────────────────
  palette: {
    mode: 'light',
    primary: {
      main:         green[500],
      light:        green[400],
      dark:         green[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main:         green[700],
      light:        green[600],
      dark:         green[800],
      contrastText: '#ffffff',
    },
    background: {
      default: paper,
      paper:   '#ffffff',
    },
    text: {
      primary:   ink[900],
      secondary: ink[600],
      disabled:  ink[400],
    },
    divider: ink[200],
    action: {
      hover:    alpha(green[500], 0.08),
      selected: alpha(green[500], 0.12),
      disabled: ink[300],
    },
    success: { main: green[500], light: green[50],  dark: green[600], contrastText: '#fff' },
    warning: { main: amber[500], light: amber[50],  dark: amber[700], contrastText: '#fff' },
    error:   { main: rose[500],  light: rose[50],   dark: rose[700],  contrastText: '#fff' },
    info:    { main: blue[500],  light: blue[50],   dark: blue[700],  contrastText: '#fff' },

    // Full custom scales — access via theme.palette.green[700] etc.
    green,
    ink,
    amber,
    rose,
    blue,
  },

  // ── Typography ─────────────────────────────────────────────────────────────
  // Only two weights used anywhere: 400 (regular) and 500 (medium).
  // fontWeightBold is intentionally 500 — no 600/700 anywhere in the design.
  typography: {
    fontFamily:        fontBody,
    fontWeightLight:   400,
    fontWeightRegular: 400,
    fontWeightMedium:  500,
    fontWeightBold:    500, // intentional — design uses 400/500 only
    fontSize:          14,

    h1: {
      fontFamily:    fontDisplay,
      fontWeight:    400,
      fontSize:      '2rem',       // 32px — dashboard greeting
      letterSpacing: '-0.02em',
      lineHeight:    1.2,
    },
    h2: {
      fontFamily:    fontDisplay,
      fontWeight:    500,
      fontSize:      '1.5rem',     // 24px
      letterSpacing: '-0.015em',
      lineHeight:    1.25,
    },
    h3: {
      fontFamily:    fontDisplay,
      fontWeight:    500,
      fontSize:      '1.25rem',    // 20px
      letterSpacing: '-0.01em',
      lineHeight:    1.3,
    },
    h4: {
      fontFamily:    fontDisplay,
      fontWeight:    500,
      fontSize:      '1.125rem',   // 18px — panel titles (.bc-title, .panel-title)
      letterSpacing: '-0.01em',
      lineHeight:    1.3,
    },
    h5: {
      fontFamily:    fontBody,
      fontWeight:    500,
      fontSize:      '1rem',
      letterSpacing: 0,
      lineHeight:    1.4,
    },
    h6: {
      fontFamily:    fontBody,
      fontWeight:    500,
      fontSize:      '0.875rem',
      letterSpacing: 0,
      lineHeight:    1.4,
    },
    subtitle1: {
      fontFamily: fontBody,
      fontWeight: 500,
      fontSize:   '0.9375rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      fontFamily: fontBody,
      fontWeight: 500,
      fontSize:   '0.875rem',
      lineHeight: 1.5,
      color:      ink[600],
    },
    body1: {
      fontFamily: fontBody,
      fontWeight: 400,
      fontSize:   '0.875rem',      // 14px desktop body
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: fontBody,
      fontWeight: 400,
      fontSize:   '0.8125rem',     // 13px
      lineHeight: 1.5,
    },
    caption: {
      fontFamily: fontBody,
      fontWeight: 400,
      fontSize:   '0.75rem',       // 12px
      lineHeight: 1.4,
      color:      ink[600],
    },
    overline: {
      fontFamily:    fontBody,
      fontWeight:    500,
      fontSize:      '0.6875rem',  // 11px — nav labels, kpi labels
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      lineHeight:    1.5,
    },
    button: {
      fontFamily:    fontBody,
      fontWeight:    500,
      fontSize:      '0.8125rem',
      textTransform: 'none',
      letterSpacing: 0,
    },

    // ── Custom variants ──────────────────────────────────────────────────────
    // Large displayed numbers: KPI values, pipeline counts, broadcast totals
    metric: {
      fontFamily:    fontDisplay,
      fontWeight:    500,
      fontSize:      '2.125rem',   // 34px — .kpi-value
      letterSpacing: '-0.02em',
      lineHeight:    1,
    },
    // Smaller displayed numbers: pipeline stage values, channel counts
    metricSm: {
      fontFamily:    fontDisplay,
      fontWeight:    500,
      fontSize:      '1.375rem',   // 22px — .pipeline-value, .channel-count
      letterSpacing: '-0.02em',
      lineHeight:    1,
    },
    // Broadcast hero metric (slightly smaller than main metric)
    metricMd: {
      fontFamily:    fontDisplay,
      fontWeight:    500,
      fontSize:      '1.875rem',   // 30px — .bc-metric-value
      letterSpacing: '-0.02em',
      lineHeight:    1,
    },
    // Panel / section titles (.panel-title, .bc-title)
    panelTitle: {
      fontFamily:    fontDisplay,
      fontWeight:    500,
      fontSize:      '1.125rem',   // 18px
      letterSpacing: '-0.01em',
      lineHeight:    1.3,
    },
    // Topbar greeting
    greeting: {
      fontFamily:    fontDisplay,
      fontWeight:    400,
      fontSize:      '1.375rem',   // 22px — .topbar-title
      letterSpacing: '-0.015em',
      lineHeight:    1.2,
    },

    // Unify fontFamily / h5 / h6 / button with the consultant dashboard.
    ...appTypography,
  },

  // ── Shape ──────────────────────────────────────────────────────────────────
  shape: {
    borderRadius: radii.md, // 10px default (--radius-md)
    radii,                  // theme.shape.radii.{sm|md|lg|xl}
  },

  // ── Component overrides ────────────────────────────────────────────────────
  components: {
    // Global baseline: font smoothing, height reset, scrollbar styling
    MuiCssBaseline: {
      styleOverrides: `
        html, body, #root { height: 100%; }
        body {
          margin: 0;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        *, *::before, *::after { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${ink[100]}; }
        ::-webkit-scrollbar-thumb {
          background: ${ink[300]};
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover { background: ${ink[400]}; }
      `,
    },

    // Map custom variant names to their default HTML elements
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          metric:     'span',
          metricSm:   'span',
          metricMd:   'span',
          panelTitle: 'h2',
          greeting:   'span',
        },
      },
    },

    // Flat paper — no elevation, no box shadow, subtle border
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow:       'none',
          border:          `1px solid ${ink[200]}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border:    `1px solid ${ink[200]}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },

    // Flat buttons — no elevation, no box shadow ever
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          boxShadow:     'none',
          borderRadius:  radii.md,
          padding:       '9px 16px',
          fontSize:      '0.8125rem',
          fontWeight:    500,
          textTransform: 'none',
          letterSpacing: 0,
          '&:hover':  { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
        },
        // Primary contained = deep green-700 (secondary brand)
        containedPrimary: {
          backgroundColor: green[700],
          color:           '#ffffff',
          '&:hover': { backgroundColor: green[800], boxShadow: 'none' },
        },
        // Outlined = ink border, ink text (matches .btn-secondary)
        outlinedPrimary: {
          borderColor:     ink[200],
          color:           ink[800],
          '&:hover': {
            borderColor:     ink[300],
            backgroundColor: ink[50],
            boxShadow:       'none',
          },
        },
        // Text / ghost = transparent with ink hover
        text: {
          color: ink[700],
          '&:hover': { backgroundColor: ink[100], boxShadow: 'none' },
        },
        sizeSmall: {
          padding:  '5px 11px',
          fontSize: '0.75rem',
        },
      },
    },

    // Icon buttons: square-ish, ink-coloured, no hover ring shadow
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          color:        ink[700],
          '&:hover': {
            backgroundColor: ink[100],
            color:           ink[900],
          },
        },
      },
    },

    // Input: rounded border, green focus ring, no shadow
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ink[300] },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: green[500],
            borderWidth: 1,
          },
        },
        notchedOutline: { borderColor: ink[200] },
        input:          { fontSize: '0.875rem', padding: '10px 12px' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: '0.875rem' },
      },
    },

    // Chips: 6px radius (--radius-sm), weight 500, flat
    // defaultProps.onClick = () => {} works around a MUI v7 Chip bug where
    // getSlotProps calls onClick(event) without a null check, crashing on
    // display-only chips. clickable={false} keeps them visually non-interactive.
    MuiChip: {
      defaultProps: {
        onClick:   () => {},
        clickable: false,
      },
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          fontWeight:   500,
          fontSize:     '0.75rem',
          height:       24,
          boxShadow:    'none',
        },
        sizeSmall: {
          height:   20,
          fontSize: '0.6875rem',
        },
      },
    },

    // Dividers match ink-200
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: ink[200] },
      },
    },

    // Flat app bar — white, ink border, no shadow
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom:    `1px solid ${ink[200]}`,
          boxShadow:       'none',
          color:           ink[900],
        },
      },
    },

    // Drawer: no shadow, ink border
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow:       'none',
          borderRight:     `1px solid ${ink[200]}`,
          backgroundColor: '#ffffff',
        },
      },
    },

    // List items: 6px radius, sentence-case, green active state
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius:  radii.sm,
          padding:       '8px 10px',
          marginBottom:  1,
          fontSize:      '0.8125rem',
          fontWeight:    500,
          '&:hover': { backgroundColor: ink[100] },
          '&.Mui-selected': {
            backgroundColor: green[500],
            color:           '#ffffff',
            '&:hover': { backgroundColor: green[600] },
            '& .MuiListItemIcon-root': { color: '#ffffff' },
            '& .MuiListItemText-primary': { color: '#ffffff' },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth:    0,
          marginRight: 10,
          color:       ink[700],
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize:   '0.8125rem',
          fontWeight: 500,
        },
      },
    },

    // Tabs: green indicator, sentence-case labels
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${ink[200]}`,
          minHeight:    40,
        },
        indicator: {
          backgroundColor: green[500],
          height:          2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight:    500,
          fontSize:      '0.8125rem',
          color:         ink[600],
          minHeight:     40,
          padding:       '10px 16px',
          '&.Mui-selected': { color: green[700] },
        },
      },
    },

    // Avatar: weight 500 initials
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize:   '0.875rem',
          fontWeight: 500,
        },
      },
    },

    // Tooltip: dark, small, sharp corners
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: ink[900],
          fontSize:        '0.75rem',
          fontWeight:      400,
          borderRadius:    radii.sm,
          padding:         '5px 10px',
        },
      },
    },

    // Badge: compact, weight 500
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize:     '0.625rem',
          fontWeight:   500,
          minWidth:     16,
          height:       16,
          padding:      '0 4px',
          borderRadius: 8,
        },
      },
    },

    // Table: thin border, ink headers in overline style
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${ink[100]}`,
          fontSize:     '0.8125rem',
          padding:      '12px 16px',
        },
        head: {
          fontWeight:      500,
          color:           ink[600],
          fontSize:        '0.6875rem',
          textTransform:   'uppercase',
          letterSpacing:   '0.08em',
          backgroundColor: ink[50],
        },
      },
    },

    // Progress bars: 4px, rounded, no shadow
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius:    2,
          backgroundColor: ink[100],
          height:          4,
          boxShadow:       'none',
        },
        bar: { borderRadius: 2 },
      },
    },

    // Dialogs: 14px radius, no elevation, ink border
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.lg,
          boxShadow:    '0 20px 60px rgba(0,0,0,0.12)',
          border:       `1px solid ${ink[200]}`,
        },
      },
    },

    // Menus: 10px radius, subtle shadow, ink border
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radii.md,
          boxShadow:    '0 8px 24px rgba(0,0,0,0.08)',
          border:       `1px solid ${ink[200]}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize:  '0.8125rem',
          padding:   '8px 14px',
          '&:hover': { backgroundColor: ink[50] },
        },
      },
    },

    // Skeleton: ink-100 base
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: ink[100] },
      },
    },

    // Select: matches input styling
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize:   '0.8125rem',
          fontWeight: 500,
        },
      },
    },
  },
});
